'use client';
import React from 'react';
import { motion } from 'framer-motion';

// Travel destination gallery with sticky scroll effect
const DestinationGallery = ({ onCtaClick }) => {
  // High quality travel images organized by column
  const galleryImages = {
    left: [
      { src: "https://images.unsplash.com/photo-1570077188670-e3a8d69ac5ff?w=600&auto=format&fit=crop", label: "Santorini" },
      { src: "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=600&auto=format&fit=crop", label: "Dubai" },
      { src: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=600&auto=format&fit=crop", label: "Alpes Suíços" },
      { src: "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=600&auto=format&fit=crop", label: "Maldivas" },
      { src: "https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?w=600&auto=format&fit=crop", label: "Lago de Montanha" },
    ],
    center: [
      { src: "https://images.unsplash.com/photo-1552733407-5d5c46c3bb3b?w=600&auto=format&fit=crop", label: "Tailândia" },
      { src: "https://images.unsplash.com/photo-1499856871958-5b9627545d1a?w=600&auto=format&fit=crop", label: "Paris" },
      { src: "https://images.unsplash.com/photo-1523906834658-6e24ef2386f9?w=600&auto=format&fit=crop", label: "Veneza" },
    ],
    right: [
      { src: "https://images.unsplash.com/photo-1539367628448-4bc5c9d171c8?w=600&auto=format&fit=crop", label: "Bali" },
      { src: "https://images.unsplash.com/photo-1493976040374-85c8e12f0c0e?w=600&auto=format&fit=crop", label: "Kyoto" },
      { src: "https://images.unsplash.com/photo-1518548419970-58e3b4079ab2?w=600&auto=format&fit=crop", label: "Seychelles" },
      { src: "https://images.unsplash.com/photo-1501785888041-af3ef285b470?w=600&auto=format&fit=crop", label: "Noruega" },
      { src: "https://images.unsplash.com/photo-1530789253388-582c481c54b0?w=600&auto=format&fit=crop", label: "Marrocos" },
    ]
  };

  return (
    <section className="bg-[#001219] relative" data-testid="destination-gallery">
      {/* Hero Banner - Sticky */}
      <div className="sticky top-0 z-10 h-screen w-full bg-gradient-to-b from-[#001219] via-[#003845] to-[#005f73] grid place-content-center">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)] bg-[size:60px_60px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]" />
        
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center px-4 relative z-10"
        >
          <span className="text-[#e9d8a6] font-medium text-sm uppercase tracking-wider">Galeria de Destinos</span>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-7xl font-bold text-white mt-4 mb-6">
            Descubra o Mundo
            <span className="block text-[#e9d8a6] mt-2">Connosco</span>
          </h2>
          <p className="text-white/70 text-lg sm:text-xl max-w-2xl mx-auto mb-8">
            Explore os destinos mais incríveis do planeta através da nossa curadoria especial
          </p>
          <div className="flex items-center justify-center gap-2 text-white/60">
            <span>Scroll para explorar</span>
            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
              </svg>
            </motion.div>
          </div>
        </motion.div>
      </div>

      {/* Gallery Grid */}
      <div className="relative z-20 bg-[#001219] py-8">
        <div className="grid grid-cols-12 gap-2 sm:gap-3 px-2 sm:px-4">
          {/* Left Column */}
          <div className="col-span-4 space-y-2 sm:space-y-3">
            {galleryImages.left.map((img, index) => (
              <motion.figure 
                key={`left-${index}`}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true, margin: "-100px" }}
                className="relative group overflow-hidden rounded-lg sm:rounded-xl"
              >
                <img
                  src={img.src}
                  alt={img.label}
                  className="w-full h-48 sm:h-72 lg:h-96 object-cover transition-transform duration-500 group-hover:scale-110"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                  <span className="text-white font-semibold text-sm sm:text-base">{img.label}</span>
                </div>
              </motion.figure>
            ))}
          </div>

          {/* Center Column - Sticky */}
          <div className="col-span-4 sticky top-20 h-[calc(100vh-5rem)] space-y-2 sm:space-y-3 py-2">
            {galleryImages.center.map((img, index) => (
              <motion.figure 
                key={`center-${index}`}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
                viewport={{ once: true }}
                className="relative group overflow-hidden rounded-lg sm:rounded-xl h-[calc(33.333%-0.5rem)]"
              >
                <img
                  src={img.src}
                  alt={img.label}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-4">
                  <span className="text-white font-semibold text-sm sm:text-lg">{img.label}</span>
                </div>
              </motion.figure>
            ))}
          </div>

          {/* Right Column */}
          <div className="col-span-4 space-y-2 sm:space-y-3">
            {galleryImages.right.map((img, index) => (
              <motion.figure 
                key={`right-${index}`}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true, margin: "-100px" }}
                className="relative group overflow-hidden rounded-lg sm:rounded-xl"
              >
                <img
                  src={img.src}
                  alt={img.label}
                  className="w-full h-48 sm:h-72 lg:h-96 object-cover transition-transform duration-500 group-hover:scale-110"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                  <span className="text-white font-semibold text-sm sm:text-base">{img.label}</span>
                </div>
              </motion.figure>
            ))}
          </div>
        </div>
      </div>

      {/* Clean CTA Footer */}
      <div className="relative z-20 bg-gradient-to-t from-[#001219] via-[#001219] to-transparent pt-16 pb-12">
        <div className="flex flex-col items-center justify-center gap-6">
          <p className="text-white/60 text-lg">Pronto para a sua próxima aventura?</p>
          <button 
            onClick={onCtaClick}
            className="bg-[#005f73] hover:bg-[#0a9396] text-white px-10 py-5 rounded-full transition-all font-medium flex items-center gap-3 text-lg shadow-lg shadow-[#005f73]/30 hover:shadow-[#0a9396]/40"
            data-testid="gallery-cta"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
            Pedir Orçamento
          </button>
        </div>
      </div>
    </section>
  );
};

export default DestinationGallery;
